import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import AlertErrorWithMessage from "../alert/AlertErrorWithMessage";
import Box from "@amzn/meridian/box";
import Heading from "@amzn/meridian/heading";
import Loader from "@amzn/meridian/loader";
import NodeIdSelect from "../nodeIdSelect/NodeIdSelect";
import BusinessSpaceSelect from "../businessSpaceSelect/BusinessSpaceSelect";
import {
  WELCOME_MESSAGE,
  NODE_ID_LABEL_TEXT,
  BUSINESS_SPACE_LABEL_TEXT,
  ALERT_DEFAULT_MESSAGE,
  ALERT_AUTHENTICATION_MESSAGE,
  USER_NAME_KEY
} from "../../constants";
import AdminContext from "../../store/admin-context";
import { useRouteBusinessSpaceSync } from "../../store/useRouteBusinessSpaceSync";
import "./styles.scss";
import AdminApiFactory from "../../admin-api/AdminApiFactory";
import AppConfigContext from "../../store/app-config";

interface AdminDetailsWithNodeInfo {
  login: string;
  nodes: string[];
}

export default function Homepage() {
  const adminContext = useContext(AdminContext);
  const appConfigContext = useContext(AppConfigContext);
  const { config, error } = appConfigContext;
  const { changeUsername } = adminContext;
  const { nodeId } = useParams<{ nodeId: string }>();
  const AdminApi = AdminApiFactory();
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState(ALERT_DEFAULT_MESSAGE);
  const [isLoading, setIsLoading] = useState(false);

  // make sure the nodeId from url is in sync with context
  // Home can render on the node-less "/:businessSpace" route, so a missing node syncs as "".
  useRouteBusinessSpaceSync(true);

  useEffect(() => {
    if(config !== null){
      setAlertMessage(ALERT_DEFAULT_MESSAGE)
      setShowAlert(false)
    }
    if(error){
      setAlertMessage("There was an error loading application features.")
      setShowAlert(true)
    }
  }, [error, config]);

  // load admin info
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const adminLoginObject: AdminDetailsWithNodeInfo = (
          await AdminApi.getAdminInfoWithNodeDetails()
        ).data as AdminDetailsWithNodeInfo;
        if (adminLoginObject) {
          // The node list is now sourced exclusively from the business-space-filtered
          // facilities endpoint (see BusinessSpaceSelect); this mount load only
          // resolves the admin's identity. Loading the unfiltered node list here would
          // contradict the business-space isolation model (LLD §6.3).
          const { login } = adminLoginObject;
          localStorage.setItem(USER_NAME_KEY, login);
          changeUsername(login);
          setShowAlert(false);
        } else {
          setShowAlert(true);
        }
      } catch (ex: any) {
        console.error("Error loading home page", ex);
        setShowAlert(true);
        // special check for 403, probably due to missing LDAP roles
        if (ex.response && ex.response.status === 403) {
          setAlertMessage(ALERT_AUTHENTICATION_MESSAGE);
        } else if (ex.message) {
          setAlertMessage(ex.message);
        }
      } finally {
        setIsLoading(false);
      }
    })();

    return () => {
      setShowAlert(false);
      setIsLoading(false);
      setAlertMessage(ALERT_DEFAULT_MESSAGE);
    };
  }, []);

  // using Meridian grid system to center element, Box is place holder, please do not delete.
  return (
    <div className="welcomeDiv">
      <Column
        alignmentHorizontal="center"
        height={450}
        heights={["20%", "fit", "fill"]}
        width="95%"
        backgroundColor="secondary"
        spacingInset="400"
      >
        <Box>
          {showAlert && <AlertErrorWithMessage message={alertMessage} />}
        </Box>
        <Box>
          <Heading level={2}>{WELCOME_MESSAGE}</Heading>
        </Box>
        <Box>
          {isLoading ? (
            <Loader />
          ) : (
            <Row>
              <BusinessSpaceSelect width={300} label={BUSINESS_SPACE_LABEL_TEXT} />
              <NodeIdSelect width={300} label={NODE_ID_LABEL_TEXT} />
            </Row>
          )}
        </Box>
      </Column>
    </div>
  );
}
